import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "../../../../../widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";
import { menuInfo } from "@shared/type/CommonType";
import { ChangeColumnConfig } from "@shared/components/GridV2/SnGridTypes";

interface Props {
  Menuprops: menuInfo;
}

const STATIC_GRID2_COLUMNS = [
  "no_wo",
  "cd_item",
  "qt_wo",
  "rt_matl",
  "qt_use",
  "tm_start",
  "tm_end",
  "tm_work",
  "nm_emp",
  "nm_equip",
];

const DYNAMIC_GRID2_COLUMNS_BY_TYPE: Record<string, string[]> = {
  cons: ["no_lot", "lot_qt_use"],
  param: [
    "nm_param",
    "dc_value_plan",
    "dc_value_run",
    "tp_grpparam",
    "yn_input",
  ],
  pack: ["no_lot", "qt"],
  recal: ["qt"],
};

const normalizeValue = (value: any) => String(value ?? "").trim();
const normalizeType = (value: any) => normalizeValue(value).toLowerCase();

const SN_PRD_WRK_REP_002_CHEM = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const searchRef = useRef<any>(null);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const focusRequestSeq = useRef(0);
  const today = new Date();
  const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
  const popDate = new Date();
  popDate.setDate(1);
  const firstDayOfMonth = `${popDate.getFullYear()}${(popDate.getMonth() + 1)
    .toString()
    .padStart(2, "0")}${popDate.getDate().toString().padStart(2, "0")}`;

  const { userInfo } = global();

  const changeGrid2VisibleColumns = (visibleColumns: Set<string>) => {
    const normalizedVisibleColumns = new Set(
      [...visibleColumns].map(normalizeType)
    );
    const applyVisible = (col: any) => ({
      ...col,
      fg_visible: normalizedVisibleColumns.has(normalizeType(col.cd_column))
        ? "Y"
        : "N",
    });
    const visibleConfig: ChangeColumnConfig[] = columns.grid2.map(
      (col: any) => ({
        cd_column: col.cd_column,
        nm_column: col.nm_column,
        fg_visible: normalizedVisibleColumns.has(normalizeType(col.cd_column))
          ? "Y"
          : "N",
      })
    );

    setColums((prev: any) => ({
      ...prev,
      grid2: prev.grid2.map(applyVisible),
    }));
    gridRefs.current["grid2"].current?.changeColumnConfigs(visibleConfig);
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearch = async () => {
    try {
      const param = {
        id: "sn_prd_wrk_rep_002_chem_01",
        params: searchRef.current.getFormdata(),
      };

      const data: any = await fnGetDataSet(param);

      setDataSources({
        grid1: data[0],
        grid2: [],
      });
      changeGrid2VisibleColumns(new Set(STATIC_GRID2_COLUMNS));
    } catch (error) {}
  };

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const onFocusedRowChangedL = async (data: any) => {
    // 왼쪽은 이제 작업그룹(mas_codel PRD732) 마스터. 선택한 cd_group을 가진
    // 모든 작업지시 실적을 그대로 조회한다(타입 혼합/행 뻥튀기 허용).
    const searchParams = searchRef.current?.getFormdata?.() ?? {};
    const params = {
      id: "sn_prd_wrk_rep_002_chem_02",
      params: {
        ...searchParams,
        cd_firm: userInfo.cd_firm,
        cd_group: data.cd_group,
      },
    };
    const requestSeq = ++focusRequestSeq.current;
    const rtn: any = await fnGetDataSet(params);
    if (requestSeq !== focusRequestSeq.current) return;

    const detailRows = rtn[0] ?? [];

    // 반환된 행의 fg_table(cons/param/pack/recal)로 동적 컬럼 결정(있는 타입의 합집합)
    const dynamicColumnSet = new Set<string>();
    detailRows.forEach((row: any) => {
      const rowType = normalizeType(row.fg_table ?? row.tp_recipe ?? row.tp_row);
      DYNAMIC_GRID2_COLUMNS_BY_TYPE[rowType]?.forEach((column) =>
        dynamicColumnSet.add(column)
      );
    });

    const visibleColumns = new Set([
      ...STATIC_GRID2_COLUMNS,
      ...dynamicColumnSet,
    ]);

    setDataSources((prev: any) => ({
      ...prev,
      grid2: detailRows,
    }));

    changeGrid2VisibleColumns(visibleColumns);
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChangedL,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
      },
    }),
    [columns, dataSources]
  );
  return (
    <div className="h-full flex flex-col">
      <div>
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex w-full h-full min-w-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="25.5%">
          <SnGrid
            // Pros로 받은 Menuprops
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            events={grids.grid1.events}
            etcProps={{
              rowSelection: "multiple",
              title: "그룹정보",
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel2" orgSize="70.5%">
          <SnGrid
            // Pros로 받은 Menuprops
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{
              rowSelection: "multiple",
              title: "파라미터정보",
            }}
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_PRD_WRK_REP_002_CHEM;
